import React, { createContext, useContext, ReactNode } from 'react';
import { 
  useHighContrastMode, 
  useReducedMotion, 
  useColorScheme,
  useAnnounce
} from '../utils/accessibility';

interface AccessibilityContextType {
  // High contrast mode
  isHighContrast: boolean;
  toggleHighContrast: () => void;
  
  // Reduced motion
  prefersReducedMotion: boolean;
  toggleReducedMotion: () => void;
  
  // Color scheme
  colorScheme: 'light' | 'dark';
  toggleColorScheme: () => void;
  setColorScheme: (scheme: 'light' | 'dark' | 'system') => void;
  
  // Screen reader announcements
  announce: (message: string, assertive?: boolean) => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export { AccessibilityContext };

/**
 * Provider component for accessibility features
 */
export const AccessibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isHighContrast, toggleHighContrast } = useHighContrastMode();
  const { prefersReducedMotion, toggleReducedMotion } = useReducedMotion();
  const { colorScheme, toggleColorScheme, setColorScheme } = useColorScheme();
  const { announce: announceToScreenReader, message } = useAnnounce();
  
  // Announce messages to screen readers
  const announce = (text: string, assertive: boolean = false) => {
    announceToScreenReader(text, assertive ? 'assertive' : 'polite');
  };
  
  const value: AccessibilityContextType = {
    isHighContrast,
    toggleHighContrast,
    prefersReducedMotion,
    toggleReducedMotion,
    colorScheme,
    toggleColorScheme,
    setColorScheme,
    announce
  };
  
  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      {/* Screen reader announcements */}
      <div 
        className="sr-only" 
        aria-live="polite" 
        aria-atomic="true"
      >
        {message}
      </div>
      <div 
        className="sr-only" 
        aria-live="assertive" 
        aria-atomic="true"
      >
        {message && message.includes('error') ? message : ''}
      </div>
    </AccessibilityContext.Provider>
  );
};

/**
 * Hook to use accessibility features
 */
export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};